import { useCallback, useEffect, useMemo, useRef } from 'react';
import useGeocodingAutocomplete from './useGeocodingAutocomplete';
import { geocodingClient } from '../services/geocoding/geocodingClient';

const EMPTY_REF_LIST = [];

const refContainsNode = (ref, node) => {
    const element = ref?.current;
    return Boolean(element && node && typeof element.contains === 'function' && element.contains(node));
};

/*
 * Headless picker for address autocomplete. Wraps `useGeocodingAutocomplete`
 * and layers on the duplicated-in-callers glue:
 *   - suggestion → `GeocodedLocation` conversion (single source of truth),
 *   - container ref + click-outside that closes the dropdown,
 *   - ArrowUp / ArrowDown / Enter / Escape keyboard navigation,
 *   - blur handler that only closes when focus leaves the picker wrapper,
 *   - derived `showNoResults` / `getActiveDescendantId` helpers for aria.
 *
 * Consumers keep control over the visual shell (plain <input>,
 * PanelInputField, etc.) and their own field-merge logic; they just bind the
 * returned handlers to their existing markup.
 */
export default function useAddressAutocompletePicker(options = {}) {
    const {
        countryCode,
        minChars = 3,
        debounceMs = 300,
        limit = 8,
        enabled = true,
        allowEmptyForwardFallback = false,
        preventEnterWithoutSelection = false,
        additionalInsideRefs = EMPTY_REF_LIST,
        onSelect,
    } = options;

    const containerRef = useRef(null);

    const autocomplete = useGeocodingAutocomplete({
        countryCode,
        minChars,
        debounceMs,
        limit,
        enabled,
        allowEmptyForwardFallback,
    });

    const {
        suggestions,
        isLoading,
        error,
        selectedSuggestion,
        isOpen,
        highlightedIndex,
        debouncedValue,
        selectSuggestion,
        setHighlightedIndex,
        openDropdown,
        closeDropdown,
    } = autocomplete;

    const pickSuggestion = useCallback((suggestion) => {
        if (!suggestion) return null;
        const geocoded = geocodingClient.toGeocodedLocation(suggestion);
        if (!geocoded) return null;
        selectSuggestion(suggestion);
        if (typeof onSelect === 'function') {
            onSelect(geocoded, suggestion);
        }
        return geocoded;
    }, [onSelect, selectSuggestion]);

    const handleKeyDown = useCallback((event) => {
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && suggestions.length > 0) {
            event.preventDefault();
            openDropdown();
            setHighlightedIndex((previous) => {
                if (event.key === 'ArrowDown') {
                    return previous < suggestions.length - 1 ? previous + 1 : 0;
                }
                return previous > 0 ? previous - 1 : suggestions.length - 1;
            });
            return;
        }
        if (event.key === 'Enter' && isOpen && highlightedIndex >= 0 && suggestions[highlightedIndex]) {
            event.preventDefault();
            pickSuggestion(suggestions[highlightedIndex]);
            return;
        }
        if (event.key === 'Enter' && preventEnterWithoutSelection && !selectedSuggestion) {
            event.preventDefault();
            return;
        }
        if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            closeDropdown();
        }
    }, [
        closeDropdown,
        highlightedIndex,
        isOpen,
        openDropdown,
        preventEnterWithoutSelection,
        pickSuggestion,
        selectedSuggestion,
        setHighlightedIndex,
        suggestions,
    ]);

    const isInsideAutocomplete = useCallback((node) => (
        refContainsNode(containerRef, node)
        || additionalInsideRefs.some((ref) => refContainsNode(ref, node))
    ), [additionalInsideRefs]);

    const handleBlur = useCallback((event) => {
        const nextFocused = event?.relatedTarget;
        if (isInsideAutocomplete(nextFocused)) return;
        closeDropdown();
    }, [closeDropdown, isInsideAutocomplete]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const handler = (event) => {
            if (!isInsideAutocomplete(event.target)) {
                closeDropdown();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen, closeDropdown, isInsideAutocomplete]);

    const showNoResults = useMemo(() => (
        isOpen
        && !isLoading
        && !error
        && debouncedValue.trim().length >= minChars
        && suggestions.length === 0
    ), [debouncedValue, error, isLoading, isOpen, minChars, suggestions.length]);

    const getActiveDescendantId = useCallback((prefix) => {
        if (!prefix) return undefined;
        if (highlightedIndex < 0 || !suggestions[highlightedIndex]) return undefined;
        return `${prefix}-option-${highlightedIndex}`;
    }, [highlightedIndex, suggestions]);

    return {
        ...autocomplete,
        containerRef,
        pickSuggestion,
        handleKeyDown,
        handleBlur,
        showNoResults,
        getActiveDescendantId,
    };
}
